import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import store, { userStore } from 'utils/store';
import config from 'utils/config';
import { hasPrivilege, SUPER_USER } from 'utils/privileges';
import changeLanguage from 'utils/i18n/changeLanguage';
import { redirectToLogin } from 'utils/auth/getLogoutPath';
import { LOGOUT_EVENT } from 'utils/auth/constants';
import { addHeapTrackingIfRequired } from 'utils/heapTracking';
import { addMindsetAIIfRequired } from 'utils/mindsetAI';
import { fetchUserProfile } from 'actions/userProfile';
import LoginActions from '../../../actions/loginActions';
import StoreActions from '../../../actions/storeActions';
import { jsx as _jsx } from "react/jsx-runtime";
var AuthContext = /*#__PURE__*/React.createContext(undefined);
function useAuthContext() {
  var context = useContext(AuthContext);
  if (context === undefined) {
    throw Error('useAuthContext must be used inside of a AuthProvider');
  }
  return context;
}
function AuthProvider(_ref) {
  var children = _ref.children;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    user = _useState2[0],
    setUser = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isAuthenticating = _useState4[0],
    setIsAuthenticating = _useState4[1];
  var navigate = useNavigate();
  var setAuthUser = useCallback(function (user) {
    if (user) {
      userStore(user);
    } else {
      store.clear();
    }
    setUser(user);
  }, []);
  useEffect(function () {
    function checkLogin() {
      return _checkLogin.apply(this, arguments);
    }
    function _checkLogin() {
      _checkLogin = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var data, language, userId, domain, domainId, isAdmin, role, _t;
        return _regeneratorRuntime.wrap(function (_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              _context.next = 1;
              return LoginActions.getLogin();
            case 1:
              data = _context.sent;
              language = data.language;
              if (language && config.locale !== language) {
                changeLanguage(language);
              }
              setAuthUser(data);

              // Add Heap Analytics and Mindset
              userId = data.id, domain = data.domain, domainId = data.domainId, isAdmin = data.isAdmin, role = data.role;
              addHeapTrackingIfRequired({
                userId: userId,
                domain: domain,
                domainId: domainId,
                isAdmin: isAdmin,
                role: role
              });
              addMindsetAIIfRequired();

              // Remove this flag to allow the app to listen to possible install events
              store.removeItem('installed');
              _context.next = 3;
              break;
            case 2:
              _context.prev = 2;
              _t = _context["catch"](0);
              setAuthUser(null);
            case 3:
              _context.prev = 3;
              setIsAuthenticating(false);
              return _context.finish(3);
            case 4:
            case "end":
              return _context.stop();
          }
        }, _callee, null, [[0, 2, 3, 4]]);
      }));
      return _checkLogin.apply(this, arguments);
    }
    checkLogin();
  }, [setAuthUser]);

  // Check whether we need to ask the user to complete their profile
  useEffect(function () {
    if (!user) {
      return;
    }
    _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var profile, superuser, userMustCompleteProfile, userHasntSeenCompleteProfile;
      return _regeneratorRuntime.wrap(function (_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 1;
            return fetchUserProfile();
          case 1:
            profile = _context2.sent;
            superuser = hasPrivilege(SUPER_USER); // Super users can access the modal by navigating directly,
            // appending ?modal=complete-profile to any store URL
            userMustCompleteProfile = profile === null || profile === void 0 ? void 0 : profile.openCompleteProfile;
            userHasntSeenCompleteProfile = (config === null || config === void 0 ? void 0 : config.enableCompleteProfile) && (profile === null || profile === void 0 ? void 0 : profile.settings) && !profile.settings.viewedCompleteProfile;
            if (!superuser && (userMustCompleteProfile || userHasntSeenCompleteProfile)) {
              navigate('?modal=complete-profile');
            }
          case 2:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }))();
  }, [user, navigate]);

  // Check for messages
  useEffect(function () {
    user && StoreActions.fetchMessages();
    if (config.serverEventsEnabled) {
      return;
    }
    function onVisibilityChange() {
      if (document.hasFocus() && user) {
        StoreActions.pollForMessages();
      } else {
        StoreActions.stopPollForMessages();
      }
    }
    window.addEventListener('focus', onVisibilityChange);
    window.addEventListener('blur', onVisibilityChange);
    return function () {
      StoreActions.stopPollForMessages();
      window.removeEventListener('focus', onVisibilityChange);
      window.removeEventListener('blur', onVisibilityChange);
    };
  }, [user]);
  useEffect(function () {
    var onStorageUpdate = function onStorageUpdate(event) {
      if (event.key === LOGOUT_EVENT) {
        setAuthUser(null);
        redirectToLogin();
      }
    };
    window.addEventListener('storage', onStorageUpdate);
    return function () {
      return window.removeEventListener('storage', onStorageUpdate);
    };
  }, [setAuthUser]);
  return /*#__PURE__*/_jsx(AuthContext.Provider, {
    value: {
      isAuthenticating: isAuthenticating,
      authUser: user,
      setAuthUser: setAuthUser
    },
    children: children
  });
}
export { AuthProvider, AuthContext, useAuthContext };